// Local-mode mocks: [{ pattern, respond(body) }], first match wins.
//
// Database → Access Logs: POST /databaseService/accessLog/list (and the
// /database/accessLog/list alias). About 120 login decisions over the last
// three days, shaped like authnull-service internal/database/accesslog Event,
// filtered and paged the way the API does it: email matches any part, the
// other text filters are exact, from inclusive, to exclusive, newest first,
// pageSize capped at 500. One reason code ("quota_exceeded") is deliberately
// not in the UI's label map, to show an unknown code rendered as itself.

const HOUR = 3600 * 1000;
const NOW = Date.now();

// Deterministic, so the table does not reshuffle on every request.
let seed = 229;
const rand = () => {
  seed = (seed * 1103515245 + 12345) % 2147483648;
  return seed / 2147483648;
};
const pick = (arr) => arr[Math.floor(rand() * arr.length)];
const hex = (n) => Array.from({ length: n }, () => Math.floor(rand() * 16).toString(16)).join("");
const uuid = () => `${hex(8)}-${hex(4)}-4${hex(3)}-a${hex(3)}-${hex(12)}`;

const PEOPLE = [
  { userId: 31, email: "sathyam@authnull.com" },
  { userId: 12, email: "aditya@authnull.com" },
  { userId: 44, email: "priya.n@authnull.com" },
  { userId: 57, email: "j.okafor@authnull.com" },
  { userId: null, email: "" }, // a login nobody could be tied to
];
const TARGETS = [
  { dbUser: "dbmfa_reader", database: "dbmfa_test", dbType: "postgres", dbHost: "192.168.122.15" },
  { dbUser: "vault_demo", database: "payments", dbType: "postgres", dbHost: "192.168.122.175" },
  { dbUser: "ca_elev", database: "payments", dbType: "postgres", dbHost: "192.168.122.175" },
  { dbUser: "app_rw", database: "orders", dbType: "mysql", dbHost: "10.20.4.11" },
  { dbUser: "report_ro", database: "analytics", dbType: "mysql", dbHost: "10.20.4.12" },
];
const CLIENT_IPS = ["192.168.122.175", "10.0.0.21", "10.0.0.44", "203.0.113.14", "198.51.100.9", "172.30.0.6"];
const POLICIES = [
  { policyId: "pol-7f3a", policyName: "test2", policyAction: "mfa_required" },
  { policyId: "pol-1b9c", policyName: "payments admins", policyAction: "mfa_required" },
  { policyId: "pol-44d0", policyName: "analytics read", policyAction: "allow" },
  { policyId: "pol-90ee", policyName: "block contractors", policyAction: "deny" },
];

// reasonCode → how the rest of the event looks. weight is relative frequency.
const OUTCOMES = [
  { reasonCode: "mfa_approved", decision: "allowed", reason: "approved", weight: 30, policy: "mfa", mfa: "approved" },
  { reasonCode: "policy_allow", decision: "allowed", reason: "allowed by policy", weight: 16, policy: "allow" },
  { reasonCode: "no_policy_allow", decision: "allowed", reason: "no policy matched; tenant default is allow", weight: 8 },
  { reasonCode: "mfa_denied", decision: "denied", reason: "push declined on the phone", weight: 10, policy: "mfa", mfa: "denied" },
  { reasonCode: "mfa_timeout", decision: "denied", reason: "push not answered in 60s", weight: 8, policy: "mfa", mfa: "timeout" },
  { reasonCode: "policy_block", decision: "denied", reason: "blocked by policy", weight: 8, policy: "deny" },
  { reasonCode: "token_refused", decision: "denied", reason: "proxy token expired", weight: 5 },
  { reasonCode: "no_policy_deny", decision: "denied", reason: "no policy matched; tenant default is deny", weight: 4 },
  { reasonCode: "condition_denied", decision: "denied", reason: "outside allowed hours (09:00 to 18:00 IST)", weight: 4, policy: "mfa" },
  { reasonCode: "approval_denied", decision: "denied", reason: "approver rejected the request", weight: 3, policy: "mfa" },
  { reasonCode: "policy_error", decision: "denied", reason: "policy engine unreachable", weight: 2 },
  { reasonCode: "error", decision: "denied", reason: "upstream database refused the connection", weight: 2 },
  { reasonCode: "other", decision: "denied", reason: "agent restarting, try again shortly", weight: 2 },
  { reasonCode: "quota_exceeded", decision: "denied", reason: "too many concurrent sessions for this role", weight: 2 },
];
const TOTAL_WEIGHT = OUTCOMES.reduce((s, o) => s + o.weight, 0);
const pickOutcome = () => {
  let r = rand() * TOTAL_WEIGHT;
  for (const o of OUTCOMES) {
    r -= o.weight;
    if (r < 0) return o;
  }
  return OUTCOMES[0];
};

function makeEvent(i) {
  const o = i < OUTCOMES.length ? OUTCOMES[i] : pickOutcome(); // every code at least once
  const person = pick(PEOPLE);
  const target = pick(TARGETS);
  const policy =
    o.policy === "mfa" ? pick(POLICIES.slice(0, 2)) : o.policy === "allow" ? POLICIES[2] : o.policy === "deny" ? POLICIES[3] : null;
  const ev = {
    eventId: uuid(),
    at: new Date(NOW - Math.floor(rand() * 72 * HOUR) - 60 * 1000).toISOString(),
    tenantId: 1,
    requestId: `req-${hex(16)}`,
    ...(person.userId ? { userId: person.userId } : {}),
    ...(person.email ? { email: person.email } : {}),
    ...target,
    clientIp: pick(CLIENT_IPS),
    decision: o.decision,
    reasonCode: o.reasonCode,
    reason: o.reason,
  };
  if (policy) Object.assign(ev, policy);
  if (o.mfa) Object.assign(ev, { mfaMethod: "push", mfaStatus: o.mfa, challengeId: 1800 + i });
  return ev;
}

const EVENTS = Array.from({ length: 120 }, (_, i) => makeEvent(i)).sort((a, b) => Date.parse(b.at) - Date.parse(a.at));

const trim = (v) => String(v ?? "").trim();

function list(body = {}) {
  const email = trim(body.email).toLowerCase();
  const dbUser = trim(body.dbUser);
  const database = trim(body.database);
  const reasonCode = trim(body.reasonCode);
  const clientIp = trim(body.clientIp);
  const decision = trim(body.decision);
  const from = trim(body.from) ? Date.parse(body.from) : null;
  const to = trim(body.to) ? Date.parse(body.to) : null;

  const rows = EVENTS.filter((e) => {
    const t = Date.parse(e.at);
    if (email && !String(e.email || "").toLowerCase().includes(email)) return false;
    if (dbUser && e.dbUser !== dbUser) return false;
    if (database && e.database !== database) return false;
    if (decision && e.decision !== decision) return false;
    if (reasonCode && e.reasonCode !== reasonCode) return false;
    if (clientIp && e.clientIp !== clientIp) return false;
    if (from != null && t < from) return false;
    if (to != null && t >= to) return false;
    return true;
  });

  const page = Math.max(1, Number(body.page) || 1);
  const pageSize = Math.min(500, Math.max(1, Number(body.pageSize) || 50));
  const start = (page - 1) * pageSize;
  return { total: rows.length, page, pageSize, events: rows.slice(start, start + pageSize) };
}

// The adapter sees config.url without the axios instance's baseURL, so the
// pattern is the path tail ("/accessLog/list"), lowercased by findMockResponse.
export const DB_ACCESS_LOG_MOCKS = [{ pattern: /\/accesslog\/list(\?|$)/, respond: (body) => list(body || {}) }];
